import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import emailjs from "@emailjs/browser";
import Notiflix from "notiflix";
import { motion, AnimatePresence, useSpring } from "framer-motion";
import {
  Mail,
  Phone,
  Building2,
  ArrowRight,
  User,
  Globe,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ShoppingCart,
  CreditCard,
  MapPin,
  Package,
  Trash2,
  Plus,
  Minus,
  Sparkles,
  Shield,
  Truck,
  Layers,
  ChevronDown,
} from "lucide-react";
import CountryCodeDropdown from "../components/CountryCodeDropdown";
import { Helmet } from "react-helmet";
import ReCAPTCHA from "react-google-recaptcha";
import { countryCodes } from "../data/countryCodes";

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Minimum order quantity (1 case = 6 bottles)
  const MIN_ORDER_QUANTITY = 6;

  // Get product data from navigation state
  const productFromState = location.state?.product;
  const [cartItems, setCartItems] = useState(
    productFromState
      ? [
          {
            ...productFromState,
            size:
              location.state?.size ||
              Object.keys(productFromState.price || {})[0] ||
              "",
            quantity: Math.max(
              location.state?.quantity || MIN_ORDER_QUANTITY,
              MIN_ORDER_QUANTITY
            ),
          },
        ]
      : []
  );

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    city: "",
    postalCode: "",
    country: "",
    orderNotes: "",
  });

  const [errors, setErrors] = useState({});
  const [isHuman, setIsHuman] = useState(false);
  const [countryCode, setCountryCode] = useState("+216");
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removingItem, setRemovingItem] = useState(null);
  const recaptchaRef = useRef(null);

  // Animated values for totals
  const subtotal = useSpring(0, { stiffness: 100, damping: 20 });
  const shipping = useSpring(0, { stiffness: 100, damping: 20 });
  const tax = useSpring(0, { stiffness: 100, damping: 20 });
  const total = useSpring(0, { stiffness: 100, damping: 20 });
  const [showCustomInput, setShowCustomInput] = useState({});

  // Display values - updated from spring values
  const [subtotalDisplay, setSubtotalDisplay] = useState("$0.00");
  const [shippingDisplay, setShippingDisplay] = useState("$0.00");
  const [taxDisplay, setTaxDisplay] = useState("$0.00");
  const [totalDisplay, setTotalDisplay] = useState("$0.00");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

    const handleScroll = () => {
      setIsVisible(window.pageYOffset > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const setSizeForItem = (index, newSize) => {
    setCartItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], size: newSize };
      return updated;
    });
  };

  // Update animated totals when cart changes and sync display values
  useEffect(() => {
    const calcSubtotal = cartItems.reduce((sum, item) => {
      return sum + (item.price?.[item.size] || 0) * item.quantity;
    }, 0);
    const calcShipping = calcSubtotal > 500 ? 0 : 25;
    const calcTax = calcSubtotal * 0.08;
    const calcTotal = calcSubtotal + calcShipping + calcTax;

    // Update spring values for animation
    subtotal.set(calcSubtotal);
    shipping.set(calcShipping);
    tax.set(calcTax);
    total.set(calcTotal);

    // Update display values immediately
    setSubtotalDisplay(`$${calcSubtotal.toFixed(2)}`);
    setShippingDisplay(`$${calcShipping.toFixed(2)}`);
    setTaxDisplay(`$${calcTax.toFixed(2)}`);
    setTotalDisplay(`$${calcTotal.toFixed(2)}`);
  }, [cartItems, subtotal, shipping, tax, total]);

  // If no products in cart, redirect to products page
  useEffect(() => {
    if (cartItems.length === 0 && !productFromState) {
      navigate("/products", { replace: true });
    }
  }, [cartItems.length, navigate, productFromState]);

  // Professional quantity options for B2B
  const quantityOptions = [
    { label: "1 Case - 6 bottles", value: 6, type: "case" },
    { label: "2 Cases - 12 bottles", value: 12, type: "case" },
    { label: "4 Cases - 24 bottles", value: 24, type: "case" },
    { label: "10 Cases - 60 bottles", value: 60, type: "bulk" },
    { label: "25 Cases - 150 bottles", value: 150, type: "bulk" },
    { label: "50 Cases - 300 bottles", value: 300, type: "bulk" },
    { label: "100 Cases - 600 bottles", value: 600, type: "bulk" },
    { label: "Custom Quantity", value: "custom", type: "custom" },
  ];

  const setQuantity = (index, quantity) => {
    if (quantity === "custom") {
      // Show custom input for this item and clear to minimum
      setShowCustomInput((prev) => ({ ...prev, [index]: true }));
      // Set to minimum or keep current if already custom
      const currentQty = cartItems[index].quantity;
      const isPresetOption = quantityOptions.find(
        (opt) => opt.value === currentQty && opt.value !== "custom"
      );

      if (isPresetOption) {
        // If coming from a preset, set to minimum
        setCartItems((prev) => {
          const updated = [...prev];
          updated[index] = { ...updated[index], quantity: MIN_ORDER_QUANTITY };
          return updated;
        });
      }
      // Otherwise keep current quantity and just show input
      return;
    }

    const numQuantity = parseInt(quantity);
    if (numQuantity < MIN_ORDER_QUANTITY) {
      Notiflix.Notify.warning(
        `Minimum order quantity is ${MIN_ORDER_QUANTITY} bottles (1 case).`,
        {
          position: "right-top",
          timeout: 3000,
        }
      );
      setCartItems((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], quantity: MIN_ORDER_QUANTITY };
        return updated;
      });
      return;
    }

    // Hide custom input when selecting a preset option
    setShowCustomInput((prev) => ({ ...prev, [index]: false }));

    setCartItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], quantity: numQuantity };
      return updated;
    });
  };
  const updateCustomQuantity = (index, value) => {
    // Allow empty value while typing
    if (value === "") {
      setCartItems((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], quantity: "" };
        return updated;
      });
      return;
    }

    const numValue = parseInt(value);

    // If invalid number, don't update
    if (isNaN(numValue)) {
      return;
    }

    // Allow any positive number while typing
    setCartItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], quantity: numValue };
      return updated;
    });
  };

  // Get selected quantity option or custom
  const getSelectedOption = (quantity) => {
    const option = quantityOptions.find((opt) => opt.value === quantity);
    return option ? option.value : "custom";
  };

  const removeItem = (index) => {
    setRemovingItem(index);
    setTimeout(() => {
      setCartItems((prev) => prev.filter((_, i) => i !== index));
      setRemovingItem(null);
    }, 300);
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.price?.[item.size] || 0;
      return total + price * item.quantity;
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const shipping = subtotal > 500 ? 0 : 25;
    const tax = subtotal * 0.08;
    return subtotal + shipping + tax;
  };

  // Input handler
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    return phone.replace(/\D/g, "").length >= 7;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = "First name must be at least 2 characters";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = "Postal code is required";
    }

    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }

    if (!isHuman) {
      newErrors.isHuman = "Please complete the reCAPTCHA verification";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      Notiflix.Notify.failure(firstError, {
        position: "right-top",
        distance: "30px",
        fontSize: "16px",
        width: "380px",
        timeout: 4000,
        useIcon: true,
        borderRadius: "12px",
      });

      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"]`
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
      }
      return false;
    }

    setErrors({});
    return true;
  };

  const sendOrder = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    const orderItems = cartItems.map((item) => ({
      title: item.title,
      size: item.size,
      quantity: item.quantity,
      price: item.price?.[item.size] || 0,
      total: (item.price?.[item.size] || 0) * item.quantity,
    }));

    const templateParams = {
      from_name: `${formData.firstName} ${formData.lastName}`,
      from_email: formData.email,
      phone_number: countryCode + formData.phone.replace(/\D/g, ""),
      company: formData.company || "N/A",
      address: formData.address,
      city: formData.city,
      postal_code: formData.postalCode,
      country: formData.country,
      order_items: orderItems
        .map(
          (item) =>
            `${item.quantity}x ${item.title} (${item.size}) - ${(
              item.quantity / 6
            ).toFixed(0)} case(s) - $${item.total.toFixed(2)}`
        )
        .join("\n"),
      subtotal: `$${calculateSubtotal().toFixed(2)}`,
      shipping: calculateSubtotal() > 500 ? "$0.00 (Free)" : "$25.00",
      tax: `$${(calculateSubtotal() * 0.08).toFixed(2)}`,
      total: `$${calculateTotal().toFixed(2)}`,
      order_notes: formData.orderNotes || "None",
      reply_to: formData.email,
    };
    try {
      await emailjs.send(
        "service_zanq9cr",
        "template_vt8fst9",
        templateParams,
        "wmEA3TtXIC6dacsNf"
      );

      setIsSubmitting(false);
      Notiflix.Report.success(
        "Order Placed Successfully!",
        "Your order has been received. We'll send you a confirmation email shortly and process your order within 24-48 hours.",
        "Great!",
        {
          width: "420px",
          borderRadius: "16px",
          svgSize: "60px",
        }
      );

      setTimeout(() => {
        navigate("/products");
      }, 2000);
    } catch (error) {
      console.error("Order sending error:", error);
      setIsSubmitting(false);
      Notiflix.Report.failure(
        "Error",
        "Failed to place your order. Please check your connection and try again.",
        "Okay",
        {
          width: "420px",
          borderRadius: "16px",
        }
      );
    }
  };

  if (cartItems.length === 0) {
    return null;
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
    exit: {
      opacity: 0,
      x: -100,
      scale: 0.8,
      transition: {
        duration: 0.3,
      },
    },
  };

  const formFieldVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff6e3] via-white to-[#fff6e3] text-black overflow-hidden relative">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-green-200/20 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-200/20 rounded-full blur-3xl"
          animate={{
            x: [0, -100, 0],
            y: [0, -50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <NavBar />
      <Helmet>
        <title>Checkout - Solivera Olive Oil</title>
        <meta
          name="description"
          content="Complete your order for premium Solivera olive oils. Secure checkout process."
        />
      </Helmet>

      <section className="py-12 sm:py-20 px-4 sm:px-6 relative z-10">
        <div className="container mx-auto max-w-7xl">
          {/* Header with enhanced animation */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center mb-12"
          >
            <motion.div
              className="inline-block mb-6"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.2,
              }}
              whileHover={{
                rotate: [0, -10, 10, -10, 0],
                scale: 1.1,
                transition: { duration: 0.5 },
              }}
            >
              <Sparkles className="w-12 h-12 text-green-500" />
            </motion.div>
            <motion.h1
              className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-green-600 via-green-500 to-yellow-500 bg-clip-text text-transparent"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              Checkout
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Complete your order for premium Tunisian olive oils
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Order Summary - Enhanced Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: -100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3, type: "spring" }}
              className="lg:col-span-1 order-2 lg:order-1"
            >
              <motion.div
                className="backdrop-blur-xl bg-white/90 rounded-3xl p-6 border border-white/30 shadow-2xl sticky top-6"
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <ShoppingCart className="w-7 h-7 text-green-600" />
                  </motion.div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    Order Summary
                  </h2>
                </motion.div>

                <AnimatePresence mode="popLayout">
                  <motion.div
                    className="space-y-4 mb-6"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {cartItems.map((item, index) => (
                      <motion.div
                        key={`${item.id}-${index}`}
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        layout
                        className={`relative bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border-2 border-gray-200 transition-all duration-300 overflow-hidden group ${
                          removingItem === index ? "opacity-0 scale-95" : ""
                        }`}
                        whileHover={{
                          y: -6,
                          boxShadow: "0 20px 40px -10px rgba(0, 0, 0, 0.15)",
                          borderColor: "#10b981",
                        }}
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 20,
                        }}
                      >
                        {/* Multi-layer hover effects */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-green-500/10 via-green-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "100%" }}
                          transition={{ duration: 0.6 }}
                        />

                        {/* Shimmer effect */}
                        <motion.div
                          className="absolute inset-0 pointer-events-none"
                          initial={{ opacity: 0 }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent opacity-0 group-hover:opacity-100"
                            animate={{
                              x: ["-100%", "200%"],
                            }}
                            transition={{
                              duration: 1.5,
                              repeat: Infinity,
                              repeatDelay: 1,
                            }}
                          />
                        </motion.div>

                        <div className="relative flex items-start gap-4">
                          <motion.img
                            src={item.image}
                            alt={item.title}
                            className="w-20 h-20 object-cover rounded-lg border-2 border-gray-100"
                            whileHover={{ scale: 1.1, rotate: 2 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-800 mb-1 truncate">
                              {item.title}
                            </h3>
                            <div className="flex items-center justify-between mb-3">
                              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                Size
                              </label>
                              <select
                                value={item.size}
                                onChange={(e) =>
                                  setSizeForItem(index, e.target.value)
                                }
                                className="px-3 py-1.5 text-sm bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-500 text-gray-800 font-medium appearance-none cursor-pointer hover:border-green-300 transition-colors"
                              >
                                {item.price &&
                                  Object.keys(item.price).map((size) => (
                                    <option key={size} value={size}>
                                      {size}
                                    </option>
                                  ))}
                              </select>
                            </div>

                            <div className="flex flex-col gap-3">
                              <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                  Quantity
                                </label>
                                <span className="text-xs text-gray-500">
                                  Min: {MIN_ORDER_QUANTITY} bottles
                                </span>
                              </div>

                              {/* Quantity Dropdown */}
                              <div className="relative">
                                <select
                                  value={getSelectedOption(item.quantity)}
                                  onChange={(e) =>
                                    setQuantity(index, e.target.value)
                                  }
                                  className="w-full px-4 py-2.5 bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-500 text-gray-800 font-medium appearance-none cursor-pointer hover:border-green-300 transition-colors"
                                >
                                  {quantityOptions.map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                      {option.type === "bulk" && " 🏆"}
                                    </option>
                                  ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                              </div>

                              {/* Custom Quantity Input (shown when custom is selected) */}
                              {(showCustomInput[index] ||
                                (getSelectedOption(item.quantity) ===
                                  "custom" &&
                                  !quantityOptions.find(
                                    (opt) => opt.value === item.quantity
                                  ))) && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="relative"
                                >
                                  <input
                                    type="number"
                                    min={MIN_ORDER_QUANTITY}
                                    value={
                                      item.quantity === "" ? "" : item.quantity
                                    }
                                    onChange={(e) =>
                                      updateCustomQuantity(
                                        index,
                                        e.target.value
                                      )
                                    }
                                    onBlur={(e) => {
                                      // Validate on blur - enforce minimum only when user leaves the field
                                      const val = parseInt(e.target.value);
                                      if (
                                        isNaN(val) ||
                                        val < MIN_ORDER_QUANTITY ||
                                        e.target.value === ""
                                      ) {
                                        updateCustomQuantity(
                                          index,
                                          MIN_ORDER_QUANTITY.toString()
                                        );
                                        Notiflix.Notify.warning(
                                          `Minimum order is ${MIN_ORDER_QUANTITY} bottles.`,
                                          {
                                            position: "right-top",
                                            timeout: 3000,
                                          }
                                        );
                                      }
                                    }}
                                    placeholder={`Enter quantity (min ${MIN_ORDER_QUANTITY} bottles)`}
                                    className="w-full px-4 py-2.5 bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-500 text-gray-800 font-medium"
                                  />

                                  <Package className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                                  <p className="text-xs text-gray-500 mt-1.5 ml-1">
                                    Enter any quantity (minimum{" "}
                                    {MIN_ORDER_QUANTITY} bottles)
                                  </p>
                                </motion.div>
                              )}
                              {/* Quantity Info Badge */}
                              <div className="flex items-center gap-2 text-xs">
                                <Layers className="w-4 h-4 text-green-600" />
                                <span className="text-gray-600">
                                  {item.quantity < 12
                                    ? `${item.quantity} bottles`
                                    : item.quantity < 60
                                    ? `${(item.quantity / 6).toFixed(0)} case${
                                        item.quantity > 6 ? "s" : ""
                                      } (${item.quantity} bottles)`
                                    : item.quantity < 300
                                    ? `Bulk order: ${item.quantity} bottles`
                                    : `Wholesale: ${item.quantity} bottles`}
                                </span>
                              </div>
                            </div>

                            {/* Price Display
                            <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-200">
                              <span className="text-sm text-gray-600">
                                Total:
                              </span>
                              <motion.span
                                className="font-bold text-gray-800 text-lg"
                                key={`${index}-${item.quantity}-${item.size}`}
                                initial={{ scale: 1.2, color: "#10b981" }}
                                animate={{ scale: 1, color: "#1f2937" }}
                                transition={{ duration: 0.2 }}
                              >
                                $
                                {(
                                  (item.price?.[item.size] || 0) * item.quantity
                                ).toFixed(2)}
                              </motion.span>
                            </div> */}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </AnimatePresence>

                {/* Price breakdown with animated values */}
                {/* <motion.div
                  className="border-t-2 border-gray-200 pt-6 space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <motion.div
                    className="flex justify-between items-center text-gray-600"
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <span className="font-medium">Subtotal</span>
                    <motion.span className="font-semibold text-gray-800">
                      {subtotalDisplay}
                    </motion.span>
                  </motion.div>
                  <motion.div
                    className="flex justify-between items-center text-gray-600"
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <span className="font-medium flex items-center gap-2">
                      Shipping
                      <Truck className="w-4 h-4" />
                    </span>
                    <motion.span className="font-semibold relative group">
                      {calculateSubtotal() > 500 ? (
                        <>
                          <motion.span
                            className="text-green-600 font-bold flex items-center gap-1"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 200 }}
                            whileHover={{ scale: 1.1 }}
                          >
                            Free 🎉
                          </motion.span>
                          <motion.div
                            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none"
                            initial={{ opacity: 0, y: 10 }}
                            whileHover={{ opacity: 1, y: 0 }}
                          >
                            You saved $25 on shipping!
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                          </motion.div>
                        </>
                      ) : (
                        shippingDisplay
                      )}
                    </motion.span>
                  </motion.div>
                  <motion.div
                    className="flex justify-between items-center text-gray-600"
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <span className="font-medium">Tax (8%)</span>
                    <motion.span className="font-semibold text-gray-800">
                      {taxDisplay}
                    </motion.span>
                  </motion.div>
                  <motion.div
                    className="border-t-2 border-gray-300 pt-4 flex justify-between items-center"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.9, type: "spring" }}
                  >
                    <span className="text-xl font-bold text-gray-800">
                      Total
                    </span>
                    <motion.span
                      className="text-2xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent"
                      animate={{
                        backgroundPosition: ["0%", "100%", "0%"],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      style={{
                        backgroundSize: "200% auto",
                      }}
                    >
                      {totalDisplay}
                    </motion.span>
                  </motion.div>
                </motion.div>

                {/* {calculateSubtotal() < 500 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: 1, type: "spring" }}
                    className="mt-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border-2 border-yellow-200"
                  >
                    <motion.p
                      className="text-sm text-center font-semibold text-gray-700"
                      animate={{
                        scale: [1, 1.02, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      🎉 Add ${(500 - calculateSubtotal()).toFixed(2)} more for{" "}
                      <span className="text-green-600 font-bold">FREE SHIPPING!</span>
                    </motion.p>
                  </motion.div>
                )} */}

                {/* Security badge */}
                <motion.div
                  className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-center gap-2 text-sm text-gray-600"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                >
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="font-medium">Secure Checkout</span>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Checkout Form - Enhanced */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: 15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.4, type: "spring" }}
              className="lg:col-span-2 order-1 lg:order-2"
            >
              <motion.div
                className="backdrop-blur-xl bg-white/90 rounded-3xl p-6 sm:p-10 border border-white/30 shadow-2xl"
                whileHover={{
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                }}
                transition={{ duration: 0.3 }}
              >
                <form onSubmit={sendOrder} className="space-y-8">
                  {/* Progress Steps */}
                  <motion.div
                    className="mb-10"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      {[
                        { label: "Personal Info", icon: User },
                        { label: "Shipping", icon: MapPin },
                        { label: "Review Order", icon: CheckCircle2 },
                      ].map((step, idx) => (
                        <React.Fragment key={step.label}>
                          <motion.div
                            className="flex flex-col items-center"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.15 }}
                          >
                            <motion.div
                              className={`w-12 h-12 rounded-full flex items-center justify-center font-bold border-2 transition-all ${
                                idx === 0
                                  ? "bg-green-500 text-white border-green-500 shadow-lg shadow-green-500/50"
                                  : idx === 1 &&
                                    Object.keys(errors).length === 0 &&
                                    formData.firstName
                                  ? "bg-green-500 text-white border-green-500"
                                  : "bg-gray-100 text-gray-400 border-gray-200"
                              }`}
                              whileHover={{ scale: 1.1, rotate: 5 }}
                              animate={
                                idx === 0
                                  ? {
                                      boxShadow: [
                                        "0 0 0 0 rgba(16, 185, 129, 0.4)",
                                        "0 0 0 10px rgba(16, 185, 129, 0)",
                                      ],
                                    }
                                  : {}
                              }
                              transition={{
                                boxShadow: {
                                  duration: 1.5,
                                  repeat: Infinity,
                                },
                              }}
                            >
                              <step.icon className="w-5 h-5" />
                            </motion.div>
                            <motion.span
                              className={`text-xs mt-2 font-medium ${
                                idx === 0 ? "text-green-600" : "text-gray-400"
                              }`}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: idx * 0.15 + 0.2 }}
                            >
                              {step.label}
                            </motion.span>
                          </motion.div>
                          {idx < 2 && (
                            <motion.div
                              className="flex-1 h-1 mx-4 bg-gray-200 rounded-full overflow-hidden"
                              initial={{ scaleX: 0 }}
                              animate={{ scaleX: 1 }}
                              transition={{
                                delay: idx * 0.15 + 0.3,
                                duration: 0.5,
                              }}
                            >
                              <motion.div
                                className={`h-full bg-green-500 rounded-full ${
                                  idx === 0 &&
                                  Object.keys(errors).length === 0 &&
                                  formData.firstName
                                    ? "w-full"
                                    : "w-0"
                                }`}
                                initial={{ width: "0%" }}
                                animate={{
                                  width:
                                    idx === 0 &&
                                    Object.keys(errors).length === 0 &&
                                    formData.firstName
                                      ? "100%"
                                      : "0%",
                                }}
                                transition={{ duration: 0.5 }}
                              />
                            </motion.div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </motion.div>
                  {/* Personal Information */}
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <motion.h2
                      className="text-2xl font-bold mb-6 flex items-center gap-3"
                      variants={itemVariants}
                    >
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{
                          duration: 20,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        <User className="w-7 h-7 text-green-600" />
                      </motion.div>
                      <span className="bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                        Personal Information
                      </span>
                    </motion.h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {[
                        {
                          field: "firstName",
                          label: "First Name",
                          placeholder: "John",
                          icon: null,
                        },
                        {
                          field: "lastName",
                          label: "Last Name",
                          placeholder: "Doe",
                          icon: null,
                        },
                      ].map(
                        ({ field, label, placeholder, icon: Icon }, idx) => (
                          <motion.div
                            key={field}
                            variants={formFieldVariants}
                            className="relative"
                          >
                            <motion.label
                              className="block text-sm font-semibold text-gray-700 mb-2"
                              whileHover={{ scale: 1.02 }}
                            >
                              {label} <span className="text-red-500">*</span>
                            </motion.label>
                            <motion.input
                              name={field}
                              type="text"
                              value={formData[field]}
                              onChange={(e) =>
                                handleChange(field, e.target.value)
                              }
                              placeholder={placeholder}
                              className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                                errors[field]
                                  ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                                  : "border-gray-200 focus:ring-green-400 focus:border-green-500 hover:border-green-300"
                              }`}
                              whileFocus={{ scale: 1.02 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            />
                            <AnimatePresence>
                              {errors[field] && (
                                <motion.p
                                  initial={{ opacity: 0, y: -10, height: 0 }}
                                  animate={{ opacity: 1, y: 0, height: "auto" }}
                                  exit={{ opacity: 0, y: -10, height: 0 }}
                                  className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                                >
                                  <AlertCircle className="w-4 h-4" />
                                  {errors[field]}
                                </motion.p>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        )
                      )}
                    </div>

                    {[
                      {
                        field: "email",
                        label: "Email",
                        placeholder: "john.doe@example.com",
                        icon: Mail,
                      },
                      {
                        field: "phone",
                        label: "Phone Number",
                        placeholder: "123456789",
                        icon: Phone,
                        isPhone: true,
                      },
                      {
                        field: "company",
                        label: "Company (Optional)",
                        placeholder: "Company Ltd.",
                        icon: Building2,
                        optional: true,
                      },
                    ].map(
                      ({
                        field,
                        label,
                        placeholder,
                        icon: Icon,
                        isPhone,
                        optional,
                      }) => (
                        <motion.div
                          key={field}
                          variants={formFieldVariants}
                          className="mt-6"
                        >
                          <motion.label
                            className="block text-sm font-semibold text-gray-700 mb-2"
                            whileHover={{ scale: 1.02 }}
                          >
                            {label}{" "}
                            {!optional && (
                              <span className="text-red-500">*</span>
                            )}
                          </motion.label>
                          <div className="relative">
                            {isPhone ? (
                              <div className="flex gap-3">
                                <CountryCodeDropdown
                                  countryCodes={countryCodes}
                                  countryCode={countryCode}
                                  setCountryCode={setCountryCode}
                                />
                                <div className="flex-1 relative">
                                  <motion.input
                                    name={field}
                                    type="tel"
                                    value={formData[field]}
                                    onChange={(e) =>
                                      handleChange(
                                        field,
                                        e.target.value.replace(/\D/g, "")
                                      )
                                    }
                                    placeholder={placeholder}
                                    className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                                      errors[field]
                                        ? "border-red-400 focus:ring-red-400"
                                        : "border-gray-200 focus:ring-green-400 hover:border-green-300"
                                    }`}
                                    whileFocus={{ scale: 1.02 }}
                                  />
                                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                </div>
                              </div>
                            ) : (
                              <>
                                <motion.input
                                  name={field}
                                  type={field === "email" ? "email" : "text"}
                                  value={formData[field]}
                                  onChange={(e) =>
                                    handleChange(field, e.target.value)
                                  }
                                  placeholder={placeholder}
                                  className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                                    errors[field]
                                      ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                                      : "border-gray-200 focus:ring-green-400 focus:border-green-500 hover:border-green-300"
                                  }`}
                                  whileFocus={{ scale: 1.02 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                  }}
                                />
                                {Icon && (
                                  <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                )}
                              </>
                            )}
                          </div>
                          <AnimatePresence>
                            {errors[field] && (
                              <motion.p
                                initial={{ opacity: 0, y: -10, height: 0 }}
                                animate={{ opacity: 1, y: 0, height: "auto" }}
                                exit={{ opacity: 0, y: -10, height: 0 }}
                                className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                              >
                                <AlertCircle className="w-4 h-4" />
                                {errors[field]}
                              </motion.p>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      )
                    )}
                  </motion.div>

                  {/* Shipping Address */}
                  <motion.div
                    className="border-t-2 border-gray-200 pt-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <motion.h2
                      className="text-2xl font-bold mb-6 flex items-center gap-3"
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      >
                        <MapPin className="w-7 h-7 text-green-600" />
                      </motion.div>
                      <span className="bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                        Shipping Address
                      </span>
                    </motion.h2>
                    <motion.div
                      className="space-y-6"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      <motion.div variants={formFieldVariants}>
                        <motion.label
                          className="block text-sm font-semibold text-gray-700 mb-2"
                          whileHover={{ scale: 1.02 }}
                        >
                          Street Address <span className="text-red-500">*</span>
                        </motion.label>
                        <div className="relative">
                          <motion.input
                            name="address"
                            type="text"
                            value={formData.address}
                            onChange={(e) =>
                              handleChange("address", e.target.value)
                            }
                            placeholder="123 Main Street"
                            className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                              errors.address
                                ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                                : "border-gray-200 focus:ring-green-400 focus:border-green-500 hover:border-green-300"
                            }`}
                            whileFocus={{ scale: 1.02 }}
                          />
                          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                        <AnimatePresence>
                          {errors.address && (
                            <motion.p
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                            >
                              <AlertCircle className="w-4 h-4" />
                              {errors.address}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </motion.div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {[
                          {
                            field: "city",
                            label: "City",
                            placeholder: "New York",
                          },
                          {
                            field: "postalCode",
                            label: "Postal Code",
                            placeholder: "10001",
                          },
                          {
                            field: "country",
                            label: "Country",
                            placeholder: "United States",
                            hasIcon: true,
                          },
                        ].map(({ field, label, placeholder, hasIcon }, idx) => (
                          <motion.div key={field} variants={formFieldVariants}>
                            <motion.label
                              className="block text-sm font-semibold text-gray-700 mb-2"
                              whileHover={{ scale: 1.02 }}
                            >
                              {label} <span className="text-red-500">*</span>
                            </motion.label>
                            <div className="relative">
                              <motion.input
                                name={field}
                                type="text"
                                value={formData[field]}
                                onChange={(e) =>
                                  handleChange(field, e.target.value)
                                }
                                placeholder={placeholder}
                                className={`w-full ${
                                  hasIcon ? "pl-12" : "px-4"
                                } pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                                  errors[field]
                                    ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                                    : "border-gray-200 focus:ring-green-400 focus:border-green-500 hover:border-green-300"
                                }`}
                                whileFocus={{ scale: 1.02 }}
                              />
                              {hasIcon && (
                                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              )}
                            </div>
                            <AnimatePresence>
                              {errors[field] && (
                                <motion.p
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                                >
                                  <AlertCircle className="w-4 h-4" />
                                  {errors[field]}
                                </motion.p>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  </motion.div>

                  {/* Order Notes */}
                  <motion.div
                    className="border-t-2 border-gray-200 pt-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                  >
                    <motion.label
                      className="block text-sm font-semibold text-gray-700 mb-2"
                      whileHover={{ scale: 1.02 }}
                    >
                      Order Notes (Optional)
                    </motion.label>
                    <motion.textarea
                      name="orderNotes"
                      value={formData.orderNotes}
                      onChange={(e) =>
                        handleChange("orderNotes", e.target.value)
                      }
                      placeholder="Any special instructions for your order..."
                      rows="4"
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 transition-all text-black placeholder-gray-400 resize-y hover:border-green-300"
                      whileFocus={{ scale: 1.01 }}
                    />
                  </motion.div>

                  {/* ReCAPTCHA and Submit */}
                  <motion.div
                    className="flex flex-col items-center gap-6 pt-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1 }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey="6LfuZhQsAAAAALemOW4yRrMjOf7xMPXBfMlW8QrV"
                        onChange={(value) => {
                          setIsHuman(!!value);
                          if (errors.isHuman) {
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.isHuman;
                              return newErrors;
                            });
                          }
                        }}
                        onExpired={() => setIsHuman(false)}
                        onError={() => {
                          setIsHuman(false);
                          Notiflix.Notify.warning(
                            "ReCAPTCHA error. Please refresh the page.",
                            { position: "right-top", timeout: 3000 }
                          );
                        }}
                      />
                    </motion.div>
                    <AnimatePresence>
                      {errors.isHuman && (
                        <motion.p
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          className="text-sm text-red-500 flex items-center gap-1"
                        >
                          <AlertCircle className="w-4 h-4" />
                          {errors.isHuman}
                        </motion.p>
                      )}
                    </AnimatePresence>

                    {/* Enhanced Submit Button */}
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className={`relative w-full sm:w-auto px-12 py-5 bg-gradient-to-r from-green-500 via-green-600 to-green-500 rounded-xl text-white font-bold text-lg uppercase tracking-wide shadow-2xl overflow-hidden ${
                        isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                      }`}
                      whileHover={
                        isSubmitting
                          ? {}
                          : {
                              scale: 1.05,
                              boxShadow:
                                "0 20px 40px -10px rgba(16, 185, 129, 0.5)",
                            }
                      }
                      whileTap={isSubmitting ? {} : { scale: 0.98 }}
                      animate={
                        isSubmitting
                          ? {
                              backgroundPosition: ["0%", "100%", "0%"],
                            }
                          : {}
                      }
                      transition={{
                        backgroundPosition: {
                          duration: 2,
                          repeat: Infinity,
                          ease: "linear",
                        },
                      }}
                      style={{
                        backgroundSize: "200% auto",
                      }}
                    >
                      {/* Animated background */}
                      {!isSubmitting && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          animate={{
                            x: ["-100%", "100%"],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                        />
                      )}
                      <span className="relative flex items-center justify-center gap-3">
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-6 h-6 animate-spin" />
                            Processing Order...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-6 h-6" />
                            Place Order
                            <ArrowRight className="w-6 h-6" />
                          </>
                        )}
                      </span>
                    </motion.button>
                  </motion.div>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced Scroll to Top */}
      <AnimatePresence>
        {isVisible && (
          <motion.button
            initial={{ opacity: 0, scale: 0, rotate: -180 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0, rotate: 180 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-6 sm:bottom-8 right-6 sm:right-8 p-4 rounded-full bg-gradient-to-r from-green-500 to-green-600 text-white shadow-2xl z-50 border-2 border-white overflow-hidden"
            whileHover={{ scale: 1.15, rotate: -90 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Scroll to top"
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-500"
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "linear",
              }}
            />
            <ArrowRight className="w-6 h-6 rotate-[-90deg] relative z-10" />
          </motion.button>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default Checkout;
